/**
 * 媒体任务「排队中 · 前面 N 单 · 约 X 分钟」的**单一口径**(1104)。
 *
 * ★字段来源:api 侧海报/视频/分身任务 DTO 的列表 + 详情接口都新增可空 `queueInfo`,
 *   只有 status=PENDING 时非 null;其他状态恒 null;**老后端不返回这个键(undefined)**。
 *   本仓 DTO 是手写在各 service 里的(generated.ts 只当补充、快照没有这个字段),
 *   所以契约类型手写在这里一份,三条线的 DTO 和 entities 层的结构化最小类型都引它 ——
 *   shared 层任何层都能 import,不会撞 FSD 单向规则。
 *
 * ★口径:
 *   · N = reason==='TENANT_CAP' ? aheadForTenant : aheadInLane;N 为 0 省略「前面 N 单」段;
 *   · etaSeconds < 60 或 reason==='STARTING' → 整句只说「排队中 · 马上开始」;
 *   · 否则向上取整到分钟:「约 15 分钟」;≥60 分钟 →「约 1 小时 10 分钟」/ 整小时「约 2 小时」;
 *   · TENANT_CAP 且 tenantCap 有值 → 附一句提示(卡片走 title tooltip,详情页正文);
 *   · 没有 queueInfo(null / undefined / 非 PENDING)→ 返回 null,调用方保持原文案一个字不改。
 */

export type MediaQueueLane = "fast" | "slow" | "bulk";
export type MediaQueueReason = "TENANT_CAP" | "LANE_BUSY" | "STARTING";

export interface MediaQueueInfo {
  lane: MediaQueueLane;
  /** 同档(全平台)排在本单前面、且不是被别户上限卡住的 PENDING 单数。 */
  aheadInLane: number;
  /** 本户同类(同 mediaType)在跑数 + 排在前面的 PENDING 数。 */
  aheadForTenant: number;
  /** 本户同类同时最多几条;null = 不限。 */
  tenantCap: number | null;
  /** 同档 worker 并发总数。 */
  capacity: number;
  /** 预计多少秒后出结果(含本单)。 */
  etaSeconds: number;
  reason: MediaQueueReason;
}

/** 前面排着几单(按 reason 取本户口径或同档口径;脏值兜 0)。 */
export function queueAheadCount(q: MediaQueueInfo): number {
  const n = q.reason === "TENANT_CAP" ? q.aheadForTenant : q.aheadInLane;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** 是否「马上开始」:STARTING 或预计不到 1 分钟。 */
function startsSoon(q: MediaQueueInfo): boolean {
  return q.reason === "STARTING" || !(q.etaSeconds >= 60);
}

/**
 * 预计时长 → 人话。<60s「马上开始」;否则向上取整到分钟。
 * 61s →「约 2 分钟」;4200s →「约 1 小时 10 分钟」;7200s →「约 2 小时」。
 */
export function formatQueueEta(etaSeconds: number): string {
  if (!(etaSeconds >= 60)) return "马上开始";
  const minutes = Math.ceil(etaSeconds / 60);
  if (minutes < 60) return `约 ${minutes} 分钟`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `约 ${h} 小时` : `约 ${h} 小时 ${m} 分钟`;
}

export interface QueueStatusText {
  /** 「排队中 · 前面 3 单 · 约 15 分钟」/「排队中 · 马上开始」。 */
  label: string;
  /** 本户上限卡住时的说明;其余情况 null。 */
  hint: string | null;
}

/**
 * 状态 + queueInfo → 排队文案。**不适用时返回 null**(非 PENDING / 没 queueInfo),
 * 调用方据此回落到原有文案。
 */
export function formatQueueStatus(
  status: string | null | undefined,
  q: MediaQueueInfo | null | undefined
): QueueStatusText | null {
  if (status !== "PENDING" || !q) return null;
  const hint =
    q.reason === "TENANT_CAP" && q.tenantCap != null
      ? `本账号同时最多 ${q.tenantCap} 条在做,前面的做完会自动开始`
      : null;
  if (startsSoon(q)) return { label: "排队中 · 马上开始", hint };
  const n = queueAheadCount(q);
  const parts = ["排队中"];
  if (n > 0) parts.push(`前面 ${n} 单`);
  parts.push(formatQueueEta(q.etaSeconds));
  return { label: parts.join(" · "), hint };
}

/** 有排队信息用排队文案,否则原样返回 `fallback`(各处原本的状态文案)。 */
export function queueAwareStatusLabel(
  status: string | null | undefined,
  q: MediaQueueInfo | null | undefined,
  fallback: string
): string {
  return formatQueueStatus(status, q)?.label ?? fallback;
}

/**
 * 刚提交、落到详情页首次拿到的 queueInfo 若显示「真在排队」(本户上限 / 同档忙,且前面有单)
 * → 提交提示改说「已提交,前面还有 N 单,轮到后自动开始」;否则 null = 保持原提示。
 */
export function queuedSubmitToast(
  status: string | null | undefined,
  q: MediaQueueInfo | null | undefined
): { title: string; description?: string } | null {
  if (status !== "PENDING" || !q) return null;
  if (q.reason !== "TENANT_CAP" && q.reason !== "LANE_BUSY") return null;
  const n = queueAheadCount(q);
  if (n <= 0) return null;
  const hint = formatQueueStatus(status, q)?.hint;
  return {
    title: `已提交,前面还有 ${n} 单,轮到后自动开始`,
    ...(hint ? { description: hint } : {}),
  };
}

/* ── 「刚提交」标记 ────────────────────────────────────────────────────────────
 * 创建页 createTask 成功后标一下 (线, taskId),详情页首次拿到任务时消费一次。
 * ★不用 navigate state:那会改动各创建页的 navigate 调用形状;也不用 storage:
 *   刷新后再弹「已提交」是假话。模块内存 Set 正好 = 「这次 SPA 导航里刚提交的」。 */
const justSubmitted = new Set<string>();

export type QueueTaskLine = "poster" | "video" | "dh";

export function markJustSubmitted(line: QueueTaskLine, taskId: number | string): void {
  justSubmitted.add(`${line}:${taskId}`);
}

/** 取并清除:同一单只认一次。 */
export function consumeJustSubmitted(line: QueueTaskLine, taskId: number | string): boolean {
  return justSubmitted.delete(`${line}:${taskId}`);
}
